/**
 * Frontend wrapper for the startMeshFromImage Cloud Function.
 *
 * Per the mesh-mode (Tripo) plan: the user clicks "Generate mesh" on an
 * uploaded image, this service POSTs `{ conversationId, imageId }` to the
 * sync HTTP function, and returns the `taskId` that identifies the
 * background mesh job. The caller then navigates to the editor and
 * subscribes to live progress via `useMeshJobProgress`.
 */

import { fetchFirebaseFunction } from '@/lib/firebaseFunctions';

// The backend function uploads the image to Tripo and creates a task,
// which usually completes in 2-5s but can briefly stall on Tripo's side.
// 90s gives generous headroom while still bounding pathological hangs.
const CLIENT_TIMEOUT_MS = 90_000;

export type StartMeshFromImagePayload = {
  conversationId: string;
  imageId: string;
};

export type StartMeshFromImageResponse = {
  taskId: string;
};

export async function startMeshFromImage(
  payload: StartMeshFromImagePayload,
): Promise<StartMeshFromImageResponse> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), CLIENT_TIMEOUT_MS);
  try {
    const res = await fetchFirebaseFunction('startMeshFromImage', {
      method: 'POST',
      body: JSON.stringify(payload),
      signal: ctrl.signal,
    });
    if (!res.ok) {
      let detail: unknown = null;
      try {
        detail = await res.json();
      } catch {
        try {
          detail = await res.text();
        } catch {
          detail = null;
        }
      }
      const msg =
        typeof detail === 'object' && detail && 'error' in detail
          ? String((detail as { error: unknown }).error)
          : typeof detail === 'string' && detail.length > 0
            ? detail
            : `HTTP ${res.status}`;
      throw new Error(`startMeshFromImage failed (${res.status}): ${msg}`);
    }
    return (await res.json()) as StartMeshFromImageResponse;
  } finally {
    clearTimeout(timer);
  }
}
